import React, { useEffect } from 'react';
import { ConsentProvider } from '@/components/ui/ConsentBanner';
import { GlobalErrorBoundary } from '@/components/ui/GlobalErrorBoundary';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import CustomCursor from "@/components/ui/CustomCursor";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./supabase-auth";
import { ProfileProvider } from "@/hooks/useProfile";
import { FamilyMembersProvider } from "@/hooks/useFamilyMembers";
import { PlacementProvider } from "@/hooks/usePlacement";
import { supabase } from "@/integrations/supabase/client";
import { ConsentBanner } from "@/components/ui/ConsentBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import { ProtectedRoute, PublicRoute } from "@/components/ui/RouteGuards";
import { AdminRoute } from "@/components/ui/AdminRoute";
import { NewAdminLayout } from "@/components/layout/NewAdminLayout";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { OrgLayout } from "@/components/layout/OrgLayout";
import { UserLayout } from "@/components/layout/UserLayout";
import { AccountLayout } from "@/components/layout/AccountLayout";
import { SkipLinks } from "@/components/ui/SkipLinks";
import { useQueryCacheManager } from "@/hooks/useQueryCacheManager";
import { Suspense, lazy } from "react";
import { RouteAnnouncer } from "@/components/ui/RouteAnnouncer";
import { useAccessibility } from "@/components/ui/AccessibilityProvider";
import { LiveRegionProvider } from "@/components/ui/LiveRegion";
import { analytics } from "@/lib/analytics";
import { MfaEnforcementGuard } from "@/components/auth/MfaEnforcementGuard";
import { ProfileErrorBoundary } from "@/components/profile/ProfileErrorBoundary";
import { OrgErrorBoundary } from "@/components/organization/OrgErrorBoundary";
import { AuthStateDebugPanel } from "@/components/debug/AuthStateDebugPanel";
import { OrgBillingRedirect } from "@/routes/OrgBillingRedirect";
import { useAuth } from "./supabase-auth";
import { logBuildInfo } from "@/lib/buildInfo";
import { assertEnvironment } from "@/lib/envValidator";
import { 
  PUBLIC_ROUTES, 
  ORG_ROUTES, 
  PROFILE_ROUTES, 
  ADMIN_ROUTES, 
  ACCOUNT_ROUTES,
  FORM_ROUTES,
  DEV_ROUTES,
  AUTH_ROUTES,
} from "@/routes/routes";

/**
 * Conditional layout wrapper for form invitation routes
 * Shows UserLayout for authenticated users, PublicLayout otherwise
 */
function ConditionalInvitationLayout({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (session) {
    return <UserLayout>{children}</UserLayout>;
  }
  
  return <PublicLayout>{children}</PublicLayout>;
}

// Lazy load all pages for better performance and code splitting
const OrgDashboard = lazy(() => import("./pages/org/OrgDashboard"));
const AdminOverview = lazy(() => import("./pages/admin/Overview"));
const AdminFunnel = lazy(() => import("./pages/admin/Funnel"));
const AdminUsage = lazy(() => import("./pages/admin/Usage"));
const AdminOrganizations = lazy(() => import("./pages/admin/Organizations"));
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminDiagnostics = lazy(() => import("./pages/admin/Diagnostics"));
const AdminBilling = lazy(() => import("./pages/admin/Billing"));
const OrgAudit = lazy(() => import("./pages/org/Audit"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ApplicationDetail = lazy(() => import("./pages/org/ApplicationDetail"));
const ApplyForm = lazy(() => import("./pages/ApplyForm"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const Login = lazy(() => import("./pages/Login"));
const GuestForm = lazy(() => import("./pages/GuestForm"));
const OrganizationSignup = lazy(() => import("./pages/OrganizationSignup"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ComplianceDashboard = lazy(() => import("./pages/ComplianceDashboard"));
const GuestFormResponse = lazy(() => import("./pages/GuestFormResponse"));
const SetupOrganization = lazy(() => import("./pages/SetupOrganization"));
// Lazy load org settings to fix dynamic import in render
const OrgSettings = lazy(() => import('./pages/org/Settings'));
const OrgBilling = lazy(() => import('./pages/org/Billing'));
const OrgAnalytics = lazy(() => import('./pages/org/Analytics'));
const OrgTeam = lazy(() => import('./pages/org/Team'));
const AccountTypeSelection = lazy(() => import("./pages/AccountTypeSelection"));

const Index = lazy(() => import("./pages/Index"));
const Landing = lazy(() => import("./pages/Landing"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Settings = lazy(() => import("./pages/Settings"));
const Vault = lazy(() => import("./pages/Vault"));
const PdfEditorPage = lazy(() => import("./pages/PdfEditorPage"));
const OrgPdfEditorPage = lazy(() => import("./pages/OrgPdfEditorPage"));
const FormInvitation = lazy(() => import("./pages/FormInvitation"));
const GuestPdfEditorPage = lazy(() => import("./pages/GuestPdfEditorPage"));
const AdvancedApplications = lazy(() => import("./pages/org/AdvancedApplications"));
const Profile = lazy(() => import("./pages/Profile"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Signup = lazy(() => import("./pages/Signup"));
const FamilyMemberEdit = lazy(() => import("./pages/FamilyMemberEdit"));
const FamilyMembers = lazy(() => import("./pages/profile/FamilyMembers"));
const ProfileBilling = lazy(() => import("./pages/profile/Billing"));
const MyForms = lazy(() => import("./pages/profile/MyForms"));
const DocumentReminders = lazy(() => import("./pages/profile/DocumentReminders"));
const IndividualAnalytics = lazy(() => import("./pages/profile/IndividualAnalytics"));
const DataExport = lazy(() => import("./pages/profile/DataExport"));
const TrustedDelegates = lazy(() => import("./pages/profile/TrustedDelegates"));
const ShareForm = lazy(() => import("./pages/profile/ShareForm"));
const ConsentManagement = lazy(() => import("./pages/profile/ConsentManagement"));
const FormInvitations = lazy(() => import("./pages/org/FormInvitations"));
const FormTemplates = lazy(() => import("./pages/org/FormTemplates"));
const FormTemplatePublish = lazy(() => import("./pages/org/FormTemplatePublish"));
const GenericApplication = lazy(() => import("./pages/GenericApplication"));
const HostedApplyForm = lazy(() => import("./pages/HostedApplyForm"));
const SchoolApplication = lazy(() => import("./pages/SchoolApplication")); // Legacy - keep for backwards compatibility
const UserApplicationDetail = lazy(() => import("./pages/UserApplicationDetail"));
const DataRequests = lazy(() => import("./pages/admin/DataRequests"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Logout = lazy(() => import("./pages/Logout"));
const MfaSetup = lazy(() => import("./pages/MfaSetup"));
const DevEmbedHarness = lazy(() => import("./pages/dev/EmbedHarness"));
const DevEmbedFrame = lazy(() => import("./pages/dev/EmbedFrame"));
const DevAnalyticsLog = lazy(() => import("./pages/dev/AnalyticsLog"));
const ConfirmEmailChange = lazy(() => import("./pages/ConfirmEmailChange"));
const BuildVersion = lazy(() => import("./pages/BuildVersion"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Verify = lazy(() => import("./pages/Verify"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Prevent unnecessary refetches on window focus/mount for heavy dashboards
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Default stale time prevents constant refetching during navigation
      staleTime: 1000 * 30, // 30 seconds - aligns with QUERY_STALE_TIME.DYNAMIC
      // Reasonable retry with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  },
});

// Log build info and validate environment on app bootstrap
logBuildInfo();
assertEnvironment();

/**
 * SECURITY: Monitors route changes and controls session recording
 * Ensures recordings only happen on approved marketing pages
 * 
 * Compliance: SOC 2 (CC6.7), GDPR Art 25 (Privacy by Design)
 */
function SessionRecordingController() {
  const location = useLocation();

  useEffect(() => {
    // Update recording state based on current route
    // This fires on every route change, ensuring dynamic control
    try {
      analytics.updateSessionRecordingForRoute(location.pathname);
    } catch (error) {
      console.error('[SessionRecordingController] Failed to update recording:', error);
      // Don't block navigation on analytics errors
    }
  }, [location.pathname]);

  // Utility component - no UI
  return null;
}

function AppRoutes() {
  // Initialize cache manager to handle logout cache clearing
  useQueryCacheManager();
  const { customCursorEnabled } = useAccessibility();

  return (
    <TooltipProvider>
      <LiveRegionProvider>
        <SkipLinks />
        <CustomCursor disabled={!customCursorEnabled} />
        <Toaster />
        <Sonner />
        {/* SECURITY FIX: Using unified ConsentBanner from ui/ConsentBanner.tsx */}
        <BrowserRouter>
          <RouteAnnouncer />
          <SessionRecordingController />
          <MfaEnforcementGuard>
            <Suspense fallback={<PageLoader />}>
              <div className="contents">
                <Routes>
        <Route path={PUBLIC_ROUTES.home} element={
          <PublicRoute>
            <Index />
          </PublicRoute>
        } />
        <Route path={PUBLIC_ROUTES.buildVersion} element={<BuildVersion />} />
        <Route path={PUBLIC_ROUTES.landing} element={<Landing />} />
              <Route path={PUBLIC_ROUTES.pricing} element={
                <PublicLayout>
                  <Pricing />
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.guest} element={
                <PublicLayout>
                  <GuestForm />
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.privacy} element={
                <PublicLayout>
                  <PrivacyPolicy />
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.terms} element={
                <PublicLayout>
                  <TermsOfService />
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.faq} element={
                <PublicLayout>
                  <FAQ />
                </PublicLayout>
              } />
              <Route path={DEV_ROUTES.embedHarness} element={
                <PublicLayout>
                  <DevEmbedHarness />
                </PublicLayout>
              } />
              <Route path={DEV_ROUTES.embedFrame} element={<DevEmbedFrame />} />
              <Route path={DEV_ROUTES.analytics} element={
                <PublicLayout>
                  <GlobalErrorBoundary>
                    <DevAnalyticsLog />
                  </GlobalErrorBoundary>
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.formToken} element={
                <PublicLayout>
                  <GuestFormResponse />
                </PublicLayout>
              } />
              
              {/* OAuth callback route */}
              <Route path={PUBLIC_ROUTES.authCallback} element={
                <PublicLayout>
                  <AuthCallback />
                </PublicLayout>
              } />
              
              {/* Logout route - always accessible */}
              <Route path={PUBLIC_ROUTES.logout} element={
                <PublicLayout>
                  <Logout />
                </PublicLayout>
              } />
              
              {/* Email change confirmation - public route */}
              <Route path={PUBLIC_ROUTES.confirmEmailChange} element={
                <ConfirmEmailChange />
              } />
              
              {/* Public routes - redirect authenticated users */}
              <Route path={PUBLIC_ROUTES.login} element={
                <PublicRoute>
                  <PublicLayout>
                    <Login />
                  </PublicLayout>
                </PublicRoute>
              } />
              <Route path={PUBLIC_ROUTES.signup} element={
                <PublicRoute>
                  <PublicLayout>
                    <Signup />
                  </PublicLayout>
                </PublicRoute>
              } />
              <Route path={PUBLIC_ROUTES.orgSignup} element={
                <PublicRoute>
                  <PublicLayout>
                    <OrganizationSignup />
                  </PublicLayout>
                </PublicRoute>
              } />
              <Route path={PUBLIC_ROUTES.forgotPassword} element={
                <PublicRoute>
                  <PublicLayout>
                    <ForgotPassword />
                  </PublicLayout>
                </PublicRoute>
              } />
              <Route path={PUBLIC_ROUTES.resetPassword} element={
                <PublicLayout>
                  <ResetPassword />
                </PublicLayout>
              } />
              <Route path={PUBLIC_ROUTES.verify} element={
                <PublicLayout>
                  <Verify />
                </PublicLayout>
              } />
              
              {/* Auth required routes */}
              <Route path={AUTH_ROUTES.accountTypeSelection} element={
                <ProtectedRoute>
                  <PublicLayout>
                    <AccountTypeSelection />
                  </PublicLayout>
                </ProtectedRoute>
              } />
              <Route path={AUTH_ROUTES.setup} element={
                <ProtectedRoute>
                  <SetupOrganization />
                </ProtectedRoute>
              } />
              
              {/* Profile Routes - For Individual Users */}
              <Route path={PROFILE_ROUTES.dashboard} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserDashboard />
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.vault} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <Vault />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.settings} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <Profile />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.account} element={
                <ProtectedRoute requireRole={['parent', 'user', 'organization_admin', 'admin']}>
                  <UserLayout>
                    <AccountSettings />
                  </UserLayout>
                </ProtectedRoute>
              } />
              {/* Redirect old /account-settings route to /profile/account */}
              <Route path="/account-settings" element={<Navigate to={PROFILE_ROUTES.account} replace />} />
              <Route path={ACCOUNT_ROUTES.settings} element={
                <ProtectedRoute>
                  <AccountLayout>
                    <Settings />
                  </AccountLayout>
                </ProtectedRoute>
              } />
              <Route path={ACCOUNT_ROUTES.security} element={
                <ProtectedRoute>
                  <AccountLayout>
                    <Navigate to={ACCOUNT_ROUTES.securityMfaSetup} replace />
                  </AccountLayout>
                </ProtectedRoute>
              } />
              <Route path={ACCOUNT_ROUTES.securityMfaSetup} element={
                <ProtectedRoute>
                  <AccountLayout>
                    <Suspense fallback={<PageLoader />}>
                      <MfaSetup />
                    </Suspense>
                  </AccountLayout>
                </ProtectedRoute>
              } />
              <Route path={ACCOUNT_ROUTES.billing} element={
                <ProtectedRoute>
                  <AccountLayout>
                    <Settings defaultTab="billing" />
                  </AccountLayout>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.family} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <FamilyMembers />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.familyMemberNew} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <FamilyMemberEdit />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.familyMemberEdit} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <FamilyMemberEdit />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.applySlug} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <ApplyForm />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              
              {/* Public hosted form route */}
              <Route path={FORM_ROUTES.applyDemoOrg} element={<DevEmbedFrame />} />
              <Route path={FORM_ROUTES.applyOrgTemplate} element={<HostedApplyForm />} />
              <Route path={PROFILE_ROUTES.application} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <GenericApplication />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.applicationSchool} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <SchoolApplication />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.forms} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <MyForms />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.formDetail} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <UserApplicationDetail />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              {/* Legacy routes - redirect to new unified forms page */}
              <Route path={PROFILE_ROUTES.applications} element={<Navigate to={PROFILE_ROUTES.forms} replace />} />
              <Route path={PROFILE_ROUTES.applicationsId} element={<Navigate to={PROFILE_ROUTES.forms} replace />} />
              <Route path={PROFILE_ROUTES.invitations} element={<Navigate to={PROFILE_ROUTES.forms} replace />} />
              <Route path={PROFILE_ROUTES.reminders} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <DocumentReminders />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.upload} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <PdfEditorPage />
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.billing} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <ProfileBilling />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.analytics} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <IndividualAnalytics />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.dataExport} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <DataExport />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.delegates} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <TrustedDelegates />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.share} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <ShareForm />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={PROFILE_ROUTES.consents} element={
                <ProtectedRoute requireRole={['parent', 'user']}>
                  <ProfileErrorBoundary>
                    <UserLayout>
                      <ConsentManagement />
                    </UserLayout>
                  </ProfileErrorBoundary>
                </ProtectedRoute>
              } />
              {/* Guest PDF editor route - accessible without auth */}
              <Route path={FORM_ROUTES.formEditor} element={
                <PublicLayout>
                  <GuestPdfEditorPage />
                </PublicLayout>
              } />
              
              {/* Form invitation route - uses conditional layout based on auth */}
              <Route path={FORM_ROUTES.formInvitation} element={
                <ConditionalInvitationLayout>
                  <FormInvitation />
                </ConditionalInvitationLayout>
              } />
              
              {/* Legacy route for backwards compatibility */}
              <Route path={PROFILE_ROUTES.invitationsToken} element={
                <ConditionalInvitationLayout>
                  <FormInvitation />
                </ConditionalInvitationLayout>
              } />
              
              {/* Org Admin Routes */}
              <Route path={ORG_ROUTES.dashboard} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <OrgDashboard />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.submissions} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <AdvancedApplications />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.submissionDetail} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <ApplicationDetail />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.invitations} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <FormInvitations />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.templates} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <FormTemplates />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.templatePublish} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <FormTemplatePublish />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.editor} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgPdfEditorPage />
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.settings} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <Suspense fallback={<PageLoader />}>
                        <OrgSettings />
                      </Suspense>
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.settingsBilling} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgBillingRedirect />
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.settingsBillingWildcard} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgBillingRedirect />
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.billing} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <Suspense fallback={<PageLoader />}>
                        <OrgBilling />
                      </Suspense>
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.analytics} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <Suspense fallback={<PageLoader />}>
                        <OrgAnalytics />
                      </Suspense>
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.audit} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <OrgAudit />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.team} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <Suspense fallback={<PageLoader />}>
                        <OrgTeam />
                      </Suspense>
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />
              <Route path={ORG_ROUTES.compliance} element={
                <ProtectedRoute requireRole={['organization_admin', 'admin']}>
                  <OrgErrorBoundary>
                    <OrgLayout>
                      <ComplianceDashboard />
                    </OrgLayout>
                  </OrgErrorBoundary>
                </ProtectedRoute>
              } />

              {/* Admin Analytics Routes */}
              <Route path={ADMIN_ROUTES.root} element={<Navigate to={ADMIN_ROUTES.overview} replace />} />
              <Route path={ADMIN_ROUTES.overview} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminOverview />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.organizations} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminOrganizations />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.events} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminEvents />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.usage} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminUsage />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.funnel} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminFunnel />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.diagnostics} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminDiagnostics />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.settings} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminSettings />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.billing} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <AdminBilling />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              <Route path={ADMIN_ROUTES.dataRequests} element={
                <AdminRoute>
                  <NewAdminLayout>
                    <DataRequests />
                  </NewAdminLayout>
                </AdminRoute>
              } />
              
              {/* All internal links now use /profile/upload directly */}
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </Suspense>
          </MfaEnforcementGuard>
        </BrowserRouter>
      </LiveRegionProvider>
    </TooltipProvider>
  );
}

function AppContent() {
  return (
    <AuthProvider supabase={supabase}>
      <ProfileProvider>
        <FamilyMembersProvider>
          <PlacementProvider>
            <AppRoutes />
            {import.meta.env.DEV && <AuthStateDebugPanel />}
          </PlacementProvider>
        </FamilyMembersProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ConsentProvider>
        <AppContent />
      </ConsentProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
